import React, { useState, useEffect } from 'react';
import { Search, Loader2 } from 'lucide-react';
import { Input } from '../UI/input';
import { Select, SelectItem } from '../UI/select';
import ProfessionalCard from './ProfessionalCard';
import { useCurrency } from '../../contexts/CurrencyContext';

// Sample industry and seniority options (same as in the profile form)
const INDUSTRY_OPTIONS = [
  'Technology',
  'Finance',
  'Healthcare',
  'Education',
  'Marketing',
  'Design',
  'Sales',
  'Consulting',
  'Legal',
  'Other'
];

const SENIORITY_OPTIONS = [
  'Entry Level',
  'Associate',
  'Mid-Level',
  'Senior',
  'Manager',
  'Director',
  'VP',
  'C-Suite',
  'Founder'
];

// Sample expertise options
const EXPERTISE_OPTIONS = [
  'JavaScript',
  'React',
  'Node.js',
  'Python',
  'Machine Learning',
  'Product Management',
  'UX Design',
  'Marketing Strategy',
  'Sales',
  'Business Development',
  'Leadership',
  'Finance',
  'Accounting',
  'Legal',
  'Healthcare',
  'Education'
];

// Professional interface
interface Professional {
  id: string;
  userId: string;
  firstName: string;
  lastName: string;
  profilePicture?: string;
  industry: string;
  seniority: string;
  expertise: string[];
  hourlyRate: number;
  bio: string;
  isVerified: boolean;
  rating: number;
  totalSessions: number;
}

// Filter state interface
interface FilterState {
  searchQuery: string;
  industry: string;
  seniority: string;
  expertiseAreas: string[];
  priceRange: [number, number];
  minRating: number;
}

interface ProfessionalSearchProps {
  professionals?: Professional[];
  loading?: boolean;
  onLoadProfessionals: (filters: FilterState) => void;
  onSendInvitation?: (professional: Professional) => void;
  onRequestChat?: (id: string) => void;
  onViewProfile?: (id: string) => void;
}

const ProfessionalSearch: React.FC<ProfessionalSearchProps> = ({
  professionals: externalProfessionals,
  loading: externalLoading,
  onLoadProfessionals,
  onSendInvitation,
  onRequestChat,
  onViewProfile
}) => {
  // Initial filter state
  const initialFilters: FilterState = {
    searchQuery: '',
    industry: '',
    seniority: '',
    expertiseAreas: [],
    priceRange: [0, 200],
    minRating: 0
  };

  // State
  const [filters, setFilters] = useState<FilterState>(initialFilters);
  const [error, setError] = useState<string | null>(null);

  // Use currency context
  const { currency, setCurrency, getCurrencySymbol } = useCurrency();

  // Use external professionals and loading state if provided
  const professionals = externalProfessionals || [];
  const loading = externalLoading || false;

  // Load professionals on initial render and when filters change
  useEffect(() => {
    // Debounce the API call
    const timeoutId = setTimeout(() => {
      onLoadProfessionals(filters);
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [filters, onLoadProfessionals]);

  // Handle text search input
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFilters(prev => ({
      ...prev,
      searchQuery: e.target.value
    }));
  };

  // Handle expertise multi-select
  const handleExpertiseChange = (_: any, values: string[]) => {
    setFilters(prev => ({
      ...prev,
      expertiseAreas: values
    }));
  };

  // Handle price range slider
  const handlePriceRangeChange = (_: any, newValue: number | number[]) => {
    setFilters(prev => ({
      ...prev,
      priceRange: newValue as [number, number]
    }));
  };

  // Handle rating slider
  const handleRatingChange = (_: any, newValue: number | number[]) => {
    setFilters(prev => ({
      ...prev,
      minRating: newValue as number
    }));
  };

  // Reset all filters
  const handleResetFilters = () => {
    setFilters(initialFilters);
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-4 sm:p-6 md:p-8">
      <div className="mx-auto max-w-4xl">
        <div className="mb-6 sm:mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3 sm:mb-4">
            <h1 className="text-2xl sm:text-3xl font-normal text-gray-900 dark:text-white">
              Find Professionals
            </h1>
            <div className="flex items-center gap-2 sm:gap-3 self-end sm:self-auto">
              <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Currency:</span>
              <Select
                value={currency}
                onValueChange={setCurrency}
                className="w-[100px] sm:w-[120px]"
              >
                <SelectItem value="GBP">£ GBP</SelectItem>
                <SelectItem value="USD">$ USD</SelectItem>
                <SelectItem value="EUR">€ EUR</SelectItem>
              </Select>
            </div>
          </div>
          <div className="relative w-full max-w-xl">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />
            <Input
              type="text"
              placeholder="Search professionals..."
              value={filters.searchQuery}
              onChange={handleSearchChange}
              className="pl-9 sm:pl-10 text-sm sm:text-base"
            />
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-12">
            <Loader2 className="h-8 w-8 sm:h-10 sm:w-10 animate-spin text-gray-400" />
          </div>
        ) : error ? (
          <div className="text-center py-8">
            <p className="text-sm sm:text-base text-red-600 dark:text-red-400">{error}</p>
          </div>
        ) : !professionals || professionals.length === 0 ? (
          <div className="text-center py-12 sm:py-16">
            <Search className="h-12 w-12 sm:h-16 sm:w-16 text-gray-400 mx-auto mb-3 sm:mb-4" />
            <p className="text-lg sm:text-xl text-gray-600 dark:text-gray-400">
              No professionals found
            </p>
            <p className="text-sm sm:text-base text-gray-500 dark:text-gray-500 mt-2">
              Try adjusting your search
            </p>
          </div>
        ) : (
          <>
            <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mb-3 sm:mb-4">
              Found {professionals?.length || 0} professional{professionals?.length !== 1 ? 's' : ''}
            </p>

            <div className="space-y-4 sm:space-y-6">
              {professionals?.map(professional => (
                <ProfessionalCard
                  key={professional.id}
                  professional={professional}
                  onSendInvitation={() => onSendInvitation?.(professional)}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ProfessionalSearch; 